/**
 * The local half of a call whose server half already succeeded (Finish and
 * Reopen). Its own module, not the hook's, so a screen test that stubs
 * `useScorebook.js` still gets the real class to test with `instanceof`.
 */

import { ApiError } from '../api/client.js';
import { OutboxSupersededError, OutboxUnavailableError } from './db.js';

/**
 * Codes that tell the client a 5xx was raised before any route handler ran, so
 * nothing can have committed. `ROLE_NOT_IMPLEMENTED` (501, the tenant
 * middleware in `apps/api/src/http/tenant.ts`) is the only one. `INTERNAL` is
 * raised both before a handler (the tenant middleware, the raw-body parser)
 * and inside one, and the client cannot tell which, so it stays hedged.
 */
const BEFORE_ANY_HANDLER: ReadonlySet<string> = new Set(['ROLE_NOT_IMPLEMENTED']);

/**
 * Whether a POST that failed may still have committed: an answer that is not
 * a 4xx (a 5xx, or a 200 whose body could not be read) and not a code raised
 * before the handler. A 4xx is a refusal, and committed nothing.
 */
export function mayHaveCommitted(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    !(error.status >= 400 && error.status < 500) &&
    !BEFORE_ANY_HANDLER.has(error.code)
  );
}

/**
 * Finish or Reopen reached the server, and this phone's copy of the game's
 * status then failed: its read, or a write that may or may not have landed
 * (`cause`). The server's side is known; this phone's is not. A stop is never
 * wrapped in this: it is thrown as itself.
 */
export class StatusNotKept extends Error {
  override readonly name = 'StatusNotKept';
  constructor(cause: unknown) {
    super('the server changed the game and this phone may not have kept it', { cause });
  }
}

/** Run `work`, turning any failure that is not a stop into `StatusNotKept`. */
export async function afterServer(work: () => Promise<void>): Promise<void> {
  try {
    await work();
  } catch (error) {
    if (error instanceof OutboxSupersededError || error instanceof OutboxUnavailableError) {
      throw error;
    }
    throw new StatusNotKept(error);
  }
}
